import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Clock, CheckCircle, Truck, XCircle, ChevronDown, ChevronUp, ShoppingBag, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { icon: Clock, color: "text-amber-500", bg: "bg-amber-50", label: "Pending" },
  confirmed: { icon: CheckCircle, color: "text-blue-500", bg: "bg-blue-50", label: "Confirmed" },
  processing: { icon: Package, color: "text-purple-500", bg: "bg-purple-50", label: "Processing" },
  shipped: { icon: Truck, color: "text-indigo-500", bg: "bg-indigo-50", label: "Shipped" },
  delivered: { icon: CheckCircle, color: "text-green-500", bg: "bg-green-50", label: "Delivered" },
  cancelled: { icon: XCircle, color: "text-red-500", bg: "bg-red-50", label: "Cancelled" }
};

const OrderCard = ({ order }) => {
  const [expanded, setExpanded] = useState(false);
  const status = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div className="bg-white border border-border rounded-lg overflow-hidden" data-testid={`order-${order.id}`}>
      {/* Order Header */}
      <div 
        className="p-4 md:p-6 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 ${status.bg} rounded-full flex items-center justify-center shrink-0`}>
              <StatusIcon className={`h-5 w-5 ${status.color}`} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Order #{order.id.slice(0, 8).toUpperCase()}</p>
              <p className="font-semibold">{formatDate(order.created_at)}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="font-bold">£{order.total.toFixed(2)}</p>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${status.bg} ${status.color}`}>
              {status.label}
            </div>
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      {/* Order Details (Expandable) */}
      {expanded && (
        <div className="border-t border-border p-4 md:p-6 bg-gray-50/50">
          {/* Items */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3">Items Ordered</h4>
            <div className="space-y-3">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-white p-3 rounded-lg border border-border">
                  {item.image_url && (
                    <img 
                      src={item.image_url} 
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-semibold">£{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping Address */}
          {order.shipping_address && (
            <div className="mb-6">
              <h4 className="font-semibold mb-2">Shipping Address</h4>
              <div className="bg-white p-3 rounded-lg border border-border text-sm">
                <p>{order.shipping_address.name || order.shipping_address.first_name}</p>
                <p>{order.shipping_address.address || order.shipping_address.address_line1}</p>
                {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                <p>{order.shipping_address.city}, {order.shipping_address.postcode}</p>
                <p>{order.shipping_address.country}</p>
              </div>
            </div>
          )}

          {/* Payment Status */}
          <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-border">
            <span className="text-sm text-muted-foreground">Payment Status</span>
            <span className={`font-medium ${order.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
              {order.payment_status === 'paid' ? 'Paid' : 'Pending'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const Orders = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setOrders(data);
        } else {
          setError('Failed to load orders');
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token]);

  // Not logged in
  if (!user) {
    return (
      <div className="pt-24 pb-16" data-testid="orders-page">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
              <LogIn className="h-10 w-10 text-muted-foreground" />
            </div>
            <h1 
              className="text-3xl font-bold mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Sign In to View Orders
            </h1>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Please sign in to your account to view your order history and track your purchases.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login">
                <Button className="rounded-none" data-testid="login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" className="rounded-none" data-testid="register-btn">
                  Create Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16" data-testid="orders-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="label-style mb-2">Your Account</p>
          <h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            My Orders
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            View your complete purchase history and track your orders.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading your orders...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Try Again
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && orders.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              No Orders Yet
            </h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              You haven't placed any orders yet. Start shopping to see your purchase history here!
            </p>
            <div className="flex flex-col gap-3 items-center">
              <Link to="/products">
                <Button className="rounded-full px-8" data-testid="shop-now-btn">
                  Start Shopping
                </Button>
              </Link>
              <Link to="/">
                <Button className="rounded-full px-8" data-testid="home-page-btn">
                  Home Page
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Orders List */}
        {!loading && !error && orders.length > 0 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Showing {orders.length} order{orders.length !== 1 ? 's' : ''}
            </p>
            {orders.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 text-center bg-secondary/30 p-8 rounded-lg">
          <h3 
            className="font-bold text-xl mb-2"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            Need Help With an Order?
          </h3>
          <p className="text-muted-foreground mb-4">
            If you have any questions about your order, please don't hesitate to contact us.
          </p>
          <Link to="/contact" className="text-primary hover:underline font-medium">
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Orders;
